<script>
import { mapGetters } from 'vuex';
import { h } from 'vue';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import { useOperators } from 'dashboard/components-next/filter/operators';

const DEFAULT_CONDITION = () => ({
  attributeKey: 'label',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
});

const nextQueryOperator = (condition, index, list) => {
  if (index === list.length - 1) return null;
  return condition.queryOperator === 'or' ? 'or' : 'and';
};

const serializeIntentConditions = conditions =>
  (conditions || [])
    .map((condition, index, list) => {
      const values = (condition.values || [])
        .map(value => (value && typeof value === 'object' ? value.id : value))
        .filter(value => value != null && value !== '');

      return {
        attribute_key: 'label',
        filter_operator: condition.filterOperator || 'equal_to',
        values,
        query_operator: nextQueryOperator(condition, index, list),
      };
    })
    .filter(condition => condition.values.length > 0);

export default {
  components: {
    LoadingState,
    SettingsSection,
    NextButton,
    ConditionRow,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { equalityOperators } = useOperators();
    return { equalityOperators };
  },
  data() {
    return {
      selectedAgentBotId: null,
      intentConditions: [],
      showAdvanced: false,
      realtimeEnabled: false,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
      labels: 'labels/getLabels',
    }),
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
    filterTypes() {
      return [
        {
          attributeKey: 'label',
          value: 'label',
          attributeName: this.$t(
            'AGENT_BOTS.BOT_CONFIGURATION.INTENTS.ATTRIBUTE_LABEL'
          ),
          label: this.$t(
            'AGENT_BOTS.BOT_CONFIGURATION.INTENTS.ATTRIBUTE_LABEL'
          ),
          inputType: 'multiSelect',
          dataType: 'text',
          filterOperators: this.equalityOperators,
          attributeModel: 'standard',
          icon: h('span', { class: 'i-lucide-tags' }),
          options: (this.labels || []).map(label => ({
            id: label.id,
            name: label.title,
          })),
        },
      ];
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot?.id;
      this.loadIntentConditionsFromInbox();
    },
    inbox: {
      immediate: true,
      handler() {
        this.loadIntentConditionsFromInbox();
        this.realtimeEnabled = this.inbox.realtime_enabled || false;
      },
    },
    labels() {
      this.loadIntentConditionsFromInbox();
    },
    realtimeEnabled(enabled) {
      if (enabled) {
        this.clearIntentConditions();
      }
    },
  },
  async mounted() {
    await this.$store.dispatch('labels/get');
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
  },

  methods: {
    clearIntentConditions() {
      this.intentConditions = [];
      this.showAdvanced = false;
    },
    loadIntentConditionsFromInbox() {
      // Realtime streaming bypasses intent gating — keep the UI exclusive.
      if (this.realtimeEnabled || this.inbox.realtime_enabled) {
        this.clearIntentConditions();
        return;
      }

      const conditions = this.inbox.required_agent_bot_intent_conditions;
      if (Array.isArray(conditions) && conditions.length > 0) {
        const hydrated = this.hydrateConditions(conditions);
        this.intentConditions = hydrated;
        this.showAdvanced = hydrated.length > 0;
        return;
      }

      // Legacy: flat label ID list
      const legacyIds = this.inbox.required_agent_bot_intents || [];
      if (legacyIds.length > 0) {
        this.intentConditions = legacyIds.map((labelId, index) => ({
          attributeKey: 'label',
          filterOperator: 'equal_to',
          values: this.labelOptionsForIds([labelId]),
          queryOperator: index === legacyIds.length - 1 ? 'and' : 'and',
        }));
        this.showAdvanced = true;
        return;
      }

      this.intentConditions = [];
    },
    hydrateConditions(conditions) {
      return conditions.map(condition => {
        const values = Array.isArray(condition.values) ? condition.values : [];
        return {
          attributeKey: condition.attribute_key || 'label',
          filterOperator: condition.filter_operator || 'equal_to',
          values: this.labelOptionsForIds(values),
          queryOperator:
            String(condition.query_operator || 'and').toLowerCase() === 'or'
              ? 'or'
              : 'and',
        };
      });
    },
    labelOptionsForIds(ids) {
      return ids
        .map(id => {
          const label = (this.labels || []).find(
            item => item.id === Number(id)
          );
          if (label) {
            return { id: label.id, name: label.title };
          }
          return { id: Number(id), name: String(id) };
        })
        .filter(option => !Number.isNaN(option.id));
    },
    appendCondition() {
      this.intentConditions = [
        ...this.intentConditions,
        DEFAULT_CONDITION(),
      ];
    },
    removeCondition(index) {
      this.intentConditions = this.intentConditions.filter(
        (_, i) => i !== index
      );
    },
    async updateActiveAgentBot() {
      try {
        const intentConditions = this.realtimeEnabled
          ? []
          : serializeIntentConditions(this.intentConditions);
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
          intentConditions,
          realtimeEnabled: this.realtimeEnabled,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form
      v-else
      class="flex flex-wrap mx-0"
      @submit.prevent="updateActiveAgentBot"
    >
      <SettingsSection
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
      >
        <div class="w-4/5">
          <label>
            <span class="flex leading-tight mb-2">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_LABEL') }}
            </span>
            <select v-model="selectedAgentBotId">
              <option value="" disabled selected>
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="agentBot in agentBots"
                :key="agentBot.id"
                :value="agentBot.id"
              >
                {{ agentBot.name }}
              </option>
            </select>
          </label>

          <div v-if="selectedAgentBotId" class="mt-6">
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="realtimeEnabled"
                type="checkbox"
                class="reset-base w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span
                class="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.REALTIME.LABEL') }}
              </span>
            </label>
            <p class="mt-1 ml-7 text-xs text-gray-500 dark:text-gray-400">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.REALTIME.DESCRIPTION') }}
            </p>
          </div>

          <div v-if="selectedAgentBotId && !realtimeEnabled" class="mt-6">
            <button
              type="button"
              class="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
              @click="showAdvanced = !showAdvanced"
            >
              <svg
                class="w-4 h-4 mr-2 transition-transform duration-200"
                :class="[showAdvanced ? 'rotate-90' : '']"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.ADVANCED.TOGGLE') }}
            </button>
          </div>

          <div
            v-if="selectedAgentBotId && !realtimeEnabled && showAdvanced"
            class="mt-4 pl-6 border-l-4 border-blue-200 dark:border-blue-600 bg-blue-50/30 dark:bg-blue-900/10 rounded-r-lg"
          >
            <div class="input-container py-4">
              <div class="flex items-center mb-2">
                <span
                  class="flex leading-tight text-blue-800 dark:text-blue-200 font-medium"
                >
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.INTENTS.LABEL') }}
                </span>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.INTENTS.DESCRIPTION') }}
              </p>

              <ul
                class="grid gap-4 list-none p-3 mb-4 outline outline-1 rounded-xl -outline-offset-1 outline-n-weak dark:outline-n-strong bg-white dark:bg-gray-800"
              >
                <template
                  v-for="(condition, i) in intentConditions"
                  :key="i"
                >
                  <ConditionRow
                    v-if="i === 0"
                    v-model:attribute-key="intentConditions[i].attributeKey"
                    v-model:filter-operator="
                      intentConditions[i].filterOperator
                    "
                    v-model:values="intentConditions[i].values"
                    :filter-types="filterTypes"
                    :show-query-operator="false"
                    @remove="removeCondition(i)"
                  />
                  <ConditionRow
                    v-else
                    v-model:attribute-key="intentConditions[i].attributeKey"
                    v-model:filter-operator="
                      intentConditions[i].filterOperator
                    "
                    v-model:query-operator="
                      intentConditions[i - 1].queryOperator
                    "
                    v-model:values="intentConditions[i].values"
                    :filter-types="filterTypes"
                    show-query-operator
                    @remove="removeCondition(i)"
                  />
                </template>
                <div>
                  <NextButton
                    type="button"
                    icon="i-lucide-plus"
                    blue
                    faded
                    sm
                    :label="
                      $t(
                        'AGENT_BOTS.BOT_CONFIGURATION.INTENTS.ADD_CONDITION'
                      )
                    "
                    @click="appendCondition"
                  />
                </div>
              </ul>

              <div
                v-if="intentConditions.length === 0"
                class="text-sm text-orange-600 dark:text-orange-400 italic mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded border border-orange-200 dark:border-orange-800"
              >
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.INTENTS.NO_INTENTS') }}
              </div>
            </div>
          </div>

          <div class="button-container space-x-2 mt-8">
            <NextButton
              type="submit"
              :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
              :is-loading="uiFlags.isSettingAgentBot"
            />
            <NextButton
              type="button"
              :disabled="!selectedAgentBotId"
              :is-loading="uiFlags.isDisconnecting"
              faded
              ruby
              @click="disconnectBot"
            >
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
            </NextButton>
          </div>
        </div>
      </SettingsSection>
    </form>
  </div>
</template>
